import { ActionIcon, Box, Flex, Menu, Text } from '@mantine/core';
import { useState } from 'react';
import Button from 'src/components/design-system/buttons/Button';
import { IconDotsVertical } from '@tabler/icons-react';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GroupedSuggestions } from 'src/common/components/markdown-editor/plugins/SuggestionTrackerPlugin';
import { ContentHeader } from 'src/UI/app-container';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/document-editor/hooks/useDocumentVersions';
import { FinaliseDocumentModal } from './FinaliseDocumentModal';
import MarkersPanel from './MarkersPanel';
import VersionTile from './VersionTile';

const emptySuggestions: GroupedSuggestions = {
  default: {},
  note: {},
  inline: {},
  placeholder: {},
  deletion: {},
};

type DocumentPanelProps = {
  backHref: string;
  title: string;
  version: string;
  versionDate: string;
  versionVariant: 'primary' | 'past';
  versions: DocumentVersion[];
  selectedVersionId?: string;
  suggestions: GroupedSuggestions;
  showMarkers: boolean;
  showCitations: boolean;
  showToggle?: boolean;
  isSaving?: boolean;
  hasChanges?: boolean;
  isReviewingDiff?: boolean;
  onShowMarkersChange: (value: boolean) => void;
  onShowCitationsChange: (value: boolean) => void;
  onSave: () => void;
  onSaveVersion: () => void;
  onRemoveVersion: (versionId: string) => void;
  onOverwriteVersion: (versionId: string) => void;
  onSelectVersion: (versionId: string) => void;
  onMakePrimary: (versionId: string) => void;
  onDownloadPdf: (fileId: number) => void;
  onFinalise: () => Promise<void>;
  onRemoveDocument: () => Promise<void>;
  removeDocumentError?: Error | null;
  onResetRemoveDocumentError?: () => void;
  isRemovingDocument?: boolean;
};

export default function DocumentPanel({
  backHref,
  title,
  version,
  versionDate,
  versionVariant,
  versions,
  selectedVersionId,
  suggestions,
  showMarkers,
  showCitations,
  showToggle = true,
  isSaving,
  hasChanges,
  isReviewingDiff,
  onShowMarkersChange,
  onShowCitationsChange,
  onSave,
  onSaveVersion,
  onRemoveVersion,
  onOverwriteVersion,
  onSelectVersion,
  onMakePrimary,
  onDownloadPdf,
  onFinalise,
  onRemoveDocument,
  removeDocumentError,
  onResetRemoveDocumentError,
  isRemovingDocument,
}: DocumentPanelProps) {
  const [finaliseModalOpen, setFinaliseModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const primaryVersion = versions.find(v => v.isPrimary);
  const secondaryVersions = versions.filter(v => !v.isPrimary);
  const selectedVersion = versions.find(v => v.id === selectedVersionId);

  return (
    <>
      <ContentHeader
        backHref={backHref}
        left={
          <Text size="sm" fw={FONT_WEIGHTS.bold} c="black">
            {title}
          </Text>
        }
        right={
          <Flex align="center">
            <VersionTile
              version={version}
              date={versionDate}
              variant={versionVariant}
              versions={versions}
              selectedVersionId={selectedVersionId}
              disabled={isReviewingDiff}
              onSaveVersion={onSaveVersion}
              onRemoveVersion={onRemoveVersion}
              onOverwriteVersion={onOverwriteVersion}
              onSelectVersion={onSelectVersion}
              onMakePrimary={onMakePrimary}
              onDownloadPdf={onDownloadPdf}
            />
            <Box w="xs" />
            <Button
              px="sm"
              size="sm"
              variant="secondary"
              onClick={onSave}
              loading={isSaving}
              disabled={!hasChanges || isReviewingDiff}
            >
              Save
            </Button>
            <Box w="xs" />
            {versionVariant === 'primary' ? (
              <Button
                px="sm"
                size="sm"
                disabled={isReviewingDiff}
                onClick={() => setFinaliseModalOpen(true)}
              >
                Finalise
              </Button>
            ) : (
              <Button
                px="sm"
                size="sm"
                disabled={isReviewingDiff}
                onClick={() => selectedVersionId && onMakePrimary(selectedVersionId)}
              >
                Set as primary
              </Button>
            )}
            <Box w="lg" />
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon id={'doc-three-dots'} variant="subtle" c="black">
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => selectedVersion?.fileId && onDownloadPdf(selectedVersion.fileId)}
                  disabled={!selectedVersion?.fileId}
                >
                  Download document
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    onResetRemoveDocumentError?.();
                    setRemoveModalOpen(true);
                  }}
                >
                  Remove document
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        }
      />

      {Object.keys(suggestions.note).length > 0 && (
        <Box mt="md">
          <MarkersPanel
            suggestions={{ ...emptySuggestions, note: suggestions.note }}
            showMarkers={showCitations}
            showToggle
            onShowMarkersChange={onShowCitationsChange}
          />
        </Box>
      )}
      {(Object.keys(suggestions.default).length > 0 ||
        Object.keys(suggestions.inline).length > 0 ||
        Object.keys(suggestions.placeholder).length > 0) && (
        <Box mt="xs">
          <MarkersPanel
            suggestions={{ ...emptySuggestions, ...suggestions, note: {} }}
            showMarkers={showMarkers}
            showToggle={showToggle}
            onShowMarkersChange={onShowMarkersChange}
          />
        </Box>
      )}

      {primaryVersion && (
        <FinaliseDocumentModal
          opened={finaliseModalOpen}
          onClose={() => setFinaliseModalOpen(false)}
          primaryVersion={primaryVersion}
          secondaryVersions={secondaryVersions}
          onConfirm={onFinalise}
        />
      )}

      <ConfirmDeleteModal
        opened={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onConfirm={async () => {
          await onRemoveDocument();
          setRemoveModalOpen(false);
        }}
        title="Remove document"
        message="Are you sure you want to remove this document? This action cannot be undone."
        confirmLabel="Remove"
        error={removeDocumentError}
        isLoading={isRemovingDocument ?? false}
      />
    </>
  );
}
